import { Component, Prop, Build } from '@stencil/core';

/**
 * Google One Tap authentication component for SignNow
 * Implements Google's One Tap sign-in functionality
 *
 * @product signnow
 * @version 1.0.0
 * @category authentication
 */

// Environment constants
const PRODUCTION_CLIENT_ID = '872266647298-89ge5l4ul3u16v5sk25joa0rd57jni17.apps.googleusercontent.com';
const DEVELOPMENT_CLIENT_ID = '799622306347-f6ahms0p90ub7o0cb2geqavlulinqr7d.apps.googleusercontent.com';
const PRODUCTION_DOMAIN = '.signnow.com';
const DEVELOPMENT_DOMAIN = '.signnow-dev.xyz';

// Helper to detect production environment based on hostname
const isProduction = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  // Production: signnow.com or mrkhub.com CDN
  return hostname.includes('signnow.com') || hostname.includes('mrkhub.com');
};

// Helper functions
const getAuthUrl = (pathSuffix = '') => {
  const baseUrl = isProduction() ? `https://app${PRODUCTION_DOMAIN}` : `https://app3${DEVELOPMENT_DOMAIN}`;
  return `${baseUrl}${pathSuffix}`;
};

const getEnvironmentSpecificValue = (productionValue: string, developmentValue: string) => (isProduction() ? productionValue : developmentValue);

const parseJwt = (token: string) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(''),
  );
  return JSON.parse(jsonPayload);
};

// Cookie helpers
const setCookie = (name: string, value: string, options: { expires?: number; domain?: string; path?: string } = {}) => {
  let cookieString = `${name}=${value}`;

  if (options.expires) {
    const date = new Date();
    date.setTime(date.getTime() + options.expires * 24 * 60 * 60 * 1000);
    cookieString += `; expires=${date.toUTCString()}`;
  }

  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }

  if (options.path) {
    cookieString += `; path=${options.path}`;
  }

  document.cookie = cookieString;
};

const removeCookie = (name: string, options: { domain?: string; path?: string } = {}) => {
  setCookie(name, '', { ...options, expires: -1 });
};

// Analytics event stubs (can be replaced with actual analytics implementation)
const onRegistration = () => {
  console.log('[Analytics] Registration event');
  window.dataLayer?.push({ event: 'registration' });
};

const onTrialPurchased = () => {
  console.log('[Analytics] Trial purchased event');
  window.dataLayer?.push({ event: 'trial_purchased' });
};

const onRegularFreeTrialActions = (provider: string, action: string) => {
  console.log(`[Analytics] Free trial action: ${provider} - ${action}`);
  window.dataLayer?.push({ event: 'free_trial_action', provider, action });
};

const onCESignUpSubmit = (email: string) => {
  console.log(`[Analytics] CE Sign-up submit: ${email}`);
  window.dataLayer?.push({ event: 'ce_signup', email });
};

const onCELoginSubmit = (email: string) => {
  console.log(`[Analytics] CE Login submit: ${email}`);
  window.dataLayer?.push({ event: 'ce_login', email });
};

// Type definitions for Google One Tap
declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: (callback?: (notification: any) => void) => void;
        };
      };
    };
  }
}

@Component({
  tag: 'sn-google-one-tap-type-1',
  shadow: false,
})
export class SNGoogleOneTapType1 {
  /**
   * Custom client ID (optional, uses environment-specific default)
   */
  @Prop() clientId?: string;

  /**
   * Disable cancel on tap outside (default: false)
   */
  @Prop() cancelOnTapOutside?: boolean = false;

  /**
   * Use FedCM for prompt (default: true)
   */
  @Prop() useFedcmForPrompt?: boolean = true;

  private pageUrl: string = '';
  private gsiLoadPromise: Promise<void> | null = null;

  componentWillLoad() {
    if (Build.isBrowser) {
      this.pageUrl = window.location.href;
    }
  }

  componentDidLoad() {
    if (!Build.isBrowser) return;
    // Remove g_state cookie
    removeCookie('g_state', { path: '/' });

    // Load GSI script and then initialize Google One Tap
    this.loadGsiScript()
      .then(() => this.initializeGoogleOneTap())
      .catch(error => {
        console.error('Failed to load Google GSI script:', error);
      });
  }

  /**
   * Load Google GSI script asynchronously using best practices
   * - Uses requestIdleCallback to avoid blocking main thread during initial load
   * - Deduplicates loading if multiple components exist on the page
   * - Falls back to setTimeout for browsers without requestIdleCallback (Safari)
   */
  private loadGsiScript(): Promise<void> {
    // GSI already loaded
    if (window.google?.accounts?.id) {
      return Promise.resolve();
    }

    // Script tag already exists (being loaded by another instance)
    const existingScript = document.querySelector('script[src*="gsi/client"]');
    if (existingScript) {
      return this.waitForGsi();
    }

    // Deduplicate concurrent load requests
    if (this.gsiLoadPromise) {
      return this.gsiLoadPromise;
    }

    this.gsiLoadPromise = new Promise((resolve, reject) => {
      const loadScript = () => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;

        script.onload = () => {
          this.waitForGsi()
            .then(resolve)
            .catch(reject);
        };

        script.onerror = () => {
          this.gsiLoadPromise = null;
          reject(new Error('Failed to load GSI script'));
        };

        document.head.appendChild(script);
      };

      // Use requestIdleCallback for non-blocking load during idle time
      // This prevents GSI from competing with critical path resources
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(loadScript, { timeout: 500 });
      } else {
        // Fallback for Safari - delay slightly to not block initial render
        setTimeout(loadScript, 100);
      }
    });

    return this.gsiLoadPromise;
  }

  /**
   * Wait for Google GSI to be fully initialized
   */
  private waitForGsi(timeout = 5000): Promise<void> {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const check = () => {
        if (window.google?.accounts?.id) {
          resolve();
        } else if (Date.now() - start > timeout) {
          reject(new Error('GSI initialization timeout'));
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }

  private initializeGoogleOneTap() {
    if (!Build.isBrowser) return;
    // GSI is guaranteed to be loaded at this point
    this.setupGoogleOneTap();
  }

  private setupGoogleOneTap() {
    if (!Build.isBrowser) return;
    const clientID = this.clientId || getEnvironmentSpecificValue(PRODUCTION_CLIENT_ID, DEVELOPMENT_CLIENT_ID);

    window.google.accounts.id.initialize({
      client_id: clientID,
      callback: this.handleCredentialResponse.bind(this),
      cancel_on_tap_outside: this.cancelOnTapOutside,
      use_fedcm_for_prompt: this.useFedcmForPrompt,
    });

    window.google.accounts.id.prompt(notification => {
      if (notification.isSkippedMoment()) {
        this.trackEvent('/api/v2/metrics/google-one-tap-skipped-me', {
          page_url: this.pageUrl,
        });
      }
    });
  }

  private async handleCredentialResponse(data: any) {
    // Track click event
    await this.trackEvent('/api/v2/metrics/google-one-tap-click-me', {
      login_status: true,
      page_url: this.pageUrl,
    });

    const pathDomain = getEnvironmentSpecificValue(PRODUCTION_DOMAIN, DEVELOPMENT_DOMAIN);

    // Remove XSRF token
    removeCookie('XSRF-TOKEN', { domain: pathDomain, path: '/' });

    const path = getAuthUrl('/auth/v1/sessions/social');

    let credentialEmail = '';
    const credential = parseJwt(data.credential);

    try {
      const response = await fetch(path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          token: data.credential,
          provider: 'google',
          login_source: 'signnow_one_tap',
        }),
      });

      const result = await response.json();
      credentialEmail = credential.email;

      console.log('result', result);

      if (result.social_reg) {
        // New registration
        onRegistration();
        onTrialPurchased();
        onRegularFreeTrialActions('Google One Tap', 'Sign-up');
        onCESignUpSubmit(credentialEmail);

        await this.trackEvent('/api/v2/metrics/google-one-tap-login-event-me', {
          login_status: true,
          page_url: this.pageUrl,
          auth_type: 'Sign-up',
        });
      } else if (result.social_reg === false) {
        // Existing user login
        onRegularFreeTrialActions('Google One Tap', 'Login');
        onCELoginSubmit(credentialEmail);
        await this.trackEvent('/api/v2/metrics/google-one-tap-login-event-me', {
          login_status: true,
          page_url: this.pageUrl,
          auth_type: 'Login',
        });
      }

      // Redirect to webapp
      if (Build.isBrowser) {
        window.location.href = getAuthUrl('/webapp/folder');
      }
    } catch (error) {
      console.error('Google One Tap authentication error:', error);

      await this.trackEvent('/api/v2/metrics/google-one-tap-fail-me', {
        login_status: false,
        page_url: this.pageUrl,
      });
    }
  }

  private async trackEvent(url: string, data: any) {
    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  render() {
    // Skip SSR - Google One Tap requires browser APIs and Google SDK
    if (!Build.isBrowser) {
      return null;
    }

    // This component doesn't render any visible content
    // Google One Tap overlay is handled by Google's SDK
    return null;
  }
}
